/**
 * 堡垒锻造音效/音频管理模块。
 *
 * 一个纯函数式、可测试的音频抽象层，位于 game-core（逻辑层）和浏览器（Web Audio API）之间。
 * game-core 从不直接导入 Phaser 或 DOM——它调用此模块，由该模块委托给当前的 provider。
 *
 * game-core 附带两个实现：
 *   - WebAudioProvider  — 真实的 Web Audio API，用于浏览器环境
 *   - NoOpProvider      — 静默桩实现，用于 Node.js / 无头测试
 *
 * provider 在创建时注入，因此任何层（渲染器、模拟框架、测试）都可以替换它而无需修改 game-core。
 */

// ─── 类型定义 ───────────────────────────────────────────────────────────────

/** 声音标识符——与资产清单中 `audio` 下的条目匹配。 */
export type SoundId = string;

/**
 * game-core 调用的 provider 接口。实现可能使用 Web Audio、howler.js、
 * HTTP 流式层或完全不做任何操作。
 *
 * 重要：此处不允许导入任何浏览器/DOM API。具体的实现位于 renderer 包中
 * （或通过动态导入在运行时 polyfill 浏览器 API）——此文件刻意保持
 * provider 无关性，以便能从 Node.js 无头测试中直接 require。
 */
export interface AudioProvider {
  /**
   * 播放一次 `soundId`。返回一个不透明的句柄，调用方可将其传给 stopSound；
   * undefined 表示" fire-and-forget（发射后不管）"。
   */
  play(soundId: SoundId, volume?: number): number | undefined;
  /** 由当前用户手势解锁 provider 音频输出；无交互环境可省略。 */
  unlock?(): Promise<boolean>;
  /** 停止由 play() 之前返回的声音；未知时不操作。 */
  stop(soundId: SoundId): void;
  /** 覆盖主输出音量 0..1。 */
  setMasterVolume(volume: number): void;
  /** 当 provider 实际上正在产生可听声音时为 true。 */
  isActive(): boolean;
}

/** 用于音频事件的最小事件总线（对 UI 回调很有用）。 */
export interface AudioListener {
  onPlay?: (soundId: SoundId) => void;
  onStop?: (soundId: SoundId) => void;
  onError?: (soundId: SoundId, error: unknown) => void;
}

// ─── 接口 / 公共 API ─────────────────────────────────────────────────────

export interface AudioSystemOptions {
  /** 要使用的 provider；不可用时默认使用 NoOpProvider。 */
  provider?: AudioProvider;
  /** 全局音量 0..1；创建时委托给 provider。 */
  masterVolume?: number;
  /** 可选的音频生命周期事件监听器。 */
  listeners?: AudioListener;
}

export interface AudioSystem {
  /** 通过 id 播放声音；如果 provider 接受则返回 true。 */
  playSound(soundId: SoundId, volume?: number): boolean;
  /** 停止之前播放的声音。 */
  stopSound(soundId: SoundId): void;
  /** 停止所有当前正在播放的声音。 */
  stopAllSounds(): void;
  /** 设置主音量 0..1。 */
  setMasterVolume(volume: number): void;
  /** 当前主音量。 */
  getMasterVolume(): number;
  /** 当活跃的 provider 不是 noop 时为 true。 */
  isAvailable(): boolean;
  /** 由当前用户手势解锁 provider 音频输出；provider 不支持时返回 false。 */
  unlock?(): Promise<boolean>;
  /** 清理所有资源；调用后系统即失效。 */
  dispose(): void;
}

// ─── NoOp Provider（Node.js / 测试的安全默认值）─────────────────────────

export class NoOpProvider implements AudioProvider {
  isActive(): boolean {
    return false;
  }
  play(_soundId: SoundId, _volume?: number): number | undefined {
    return undefined;
  }
  stop(_soundId: SoundId): void {
    // 不操作
  }
  setMasterVolume(_volume: number): void {
    // 不操作
  }
}

// ─── AudioSystem 实现 ─────────────────────────────────────────────────

/**
 * AudioProvider 的薄封装。
 *
 * 将所有调用委托给 provider，并触发可选的生命周期事件，
 * 以便 UI 做出响应（例如播放声音时静音音乐）。
 */
export class AudioSystemImpl implements AudioSystem {
  private readonly provider: AudioProvider;
  private readonly listeners?: AudioListener;
  private _masterVolume = 1;

  constructor(options: AudioSystemOptions = {}) {
    this.provider = options.provider ?? new NoOpProvider();
    this.listeners = options.listeners;
    this._masterVolume = options.masterVolume ?? 1;
    this.provider.setMasterVolume(this._masterVolume);
  }

  playSound(soundId: SoundId, volume?: number): boolean {
    try {
      const handle = this.provider.play(soundId, volume);
      this.listeners?.onPlay?.(soundId);
      return handle !== undefined;
    } catch (err) {
      this.listeners?.onError?.(soundId, err);
      return false;
    }
  }

  stopSound(soundId: SoundId): void {
    try {
      this.provider.stop(soundId);
      this.listeners?.onStop?.(soundId);
    } catch (err) {
      this.listeners?.onError?.(soundId, err);
    }
  }

  stopAllSounds(): void {
    // NoOp provider 不跟踪句柄，因此我们直接委托。
    // 对于跟踪句柄的 provider，调用单独停止。
    this.provider.stop("");
  }

  setMasterVolume(volume: number): void {
    this._masterVolume = Math.max(0, Math.min(1, volume));
    this.provider.setMasterVolume(this._masterVolume);
  }

  getMasterVolume(): number {
    return this._masterVolume;
  }

  isAvailable(): boolean {
    return this.provider.isActive();
  }

  async unlock(): Promise<boolean> {
    if (!this.provider.unlock) return false;
    try {
      return await this.provider.unlock();
    } catch {
      return false;
    }
  }

  dispose(): void {
    // NoOp 无需释放；真实 provider 可在此实现清理逻辑。
  }
}

// ─── 工厂方法 ────────────────────────────────────────────────────────────

/**
 * 创建 AudioSystem，选择最佳可用的 provider。
 *
 * 在 Node.js / 无头环境中始终返回 NoOpProvider。
 * 在浏览器中，真实的 WebAudioProvider 可在稍后由更高层（renderer 包）接入——
 * 此函数仅暴露接口契约，而非浏览器特定的接线逻辑。
 */
export function createAudioSystem(options: AudioSystemOptions = {}): AudioSystem {
  return new AudioSystemImpl(options);
}

/** 每个测试安全音频系统返回的哨兵值。 */
export const NO_OP_SYSTEM: AudioSystem = new AudioSystemImpl({
  provider: new NoOpProvider(),
});
